import FormHeader from "@/components/FormHeader";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  getListPeriode,
  getListProgramStudentExchange,
} from "@/services/getData";
import { Periode, RefDaftarProgram } from "@/types";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight } from "lucide-react";
import React, { useState } from "react";
import { LuListFilter } from "react-icons/lu";
import { useNavigate, useSearchParams } from "react-router-dom";

export default function FormFilter({ closeModal }: { closeModal: () => void }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const idsUk = searchParams.get("ids_uk");
  const idsSysPeriode = searchParams.get("ids_sys_periode");
  const statusValidasi = searchParams.get("status_validasi");
  const statusKelulusan = searchParams.get("status_kelulusan");
  const statusDaftarkembali = searchParams.get("status_daftarkembali");

  const [selectedFilter, setSelectedFilter] = useState("PROGRAM");
  const [filter, setFilter] = useState<{
    program: string[];
    gelombang: string[];
    status_validasi: string;
    status_kelulusan: string;
    status_daftarkembali: string;
  }>({
    program: idsUk?.split("-") || [],
    gelombang: idsSysPeriode?.split("-") || [],
    status_validasi: statusValidasi || "",
    status_kelulusan: statusKelulusan || "",
    status_daftarkembali: statusDaftarkembali || "",
  });
  const [searchQueryProdi, setSearchQueryProdi] = useState("");

  const { data: listPeriode, isPending: isPendingListPeriode } = useQuery({
    queryKey: ["listPeriode"],
    queryFn: () => getListPeriode(),
  });
  const listPeriodeStudentExchange =
    listPeriode &&
    listPeriode.filter((periode: Periode) => {
      return periode.jalur_jenis?.nama_jalur_jenis_slug === "studentexchange";
    });

  const {
    data: dataProgramStudentExchange,
    isPending: isPendingDataProgramStudentExchange,
  } = useQuery({
    queryKey: ["listProgramStudentExchange"],
    queryFn: getListProgramStudentExchange,
  });

  const handleApplyFilter = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (filter.gelombang.length > 0) {
      searchParams.set("ids_sys_periode", filter.gelombang.join("-"));
    } else {
      searchParams.delete("ids_sys_periode");
    }

    if (filter.program.length > 0) {
      searchParams.set("ids_ref_daftar_program", filter.program.join("-"));
    } else {
      searchParams.delete("ids_ref_daftar_program");
    }

    if (filter.status_validasi.length > 0) {
      searchParams.set("status_validasi", filter.status_validasi);
    } else {
      searchParams.delete("status_validasi");
    }

    if (filter.status_kelulusan.length > 0) {
      searchParams.set("status_kelulusan", filter.status_kelulusan);
    } else {
      searchParams.delete("status_kelulusan");
    }

    if (filter.status_daftarkembali.length > 0) {
      searchParams.set("status_daftarkembali", filter.status_daftarkembali);
    } else {
      searchParams.delete("status_daftarkembali");
    }

    navigate(
      `/operator-studentexchange/data-pendaftaran?${searchParams.toString()}`,
    );
    closeModal();
  };

  return (
    <form
      onSubmit={handleApplyFilter}
      className="pb-20 relative flex flex-col gap-y-3"
    >
      <FormHeader title="Filter Data Pendaftaran" icon={<LuListFilter />} />

      <section className="p-3 relative border rounded-xl flex flex-col gap-y-3">
        <span className="font-bold">Status</span>
        <div className="grid grid-cols-2 gap-3">
          <div className="flex items-center gap-3">
            <Checkbox
              checked={filter.status_validasi == "0"}
              onCheckedChange={(checked) =>
                setFilter((prev) => ({
                  ...prev,
                  status_validasi: checked ? "0" : "",
                }))
              }
            />
            <Label>Belum Validasi Pendaftaran</Label>
          </div>
          <div className="flex items-center gap-3">
            <Checkbox
              checked={filter.status_validasi == "1"}
              onCheckedChange={(checked) =>
                setFilter((prev) => ({
                  ...prev,
                  status_validasi: checked ? "1" : "",
                }))
              }
            />
            <Label>Sudah Validasi Pendaftaran</Label>
          </div>
        </div>
      </section>

      <section className="p-3 relative border rounded-xl">
        <div
          className="flex justify-between items-center"
          onClick={() =>
            setSelectedFilter((prev) =>
              prev === "GELOMBANG" ? "" : "GELOMBANG",
            )
          }
        >
          <span className="font-bold">Gelombang</span>
          <Button type="button" size={"icon"} variant={"outline"}>
            <ChevronRight
              className={`${
                selectedFilter === "GELOMBANG" ? "rotate-90" : ""
              } duration-300`}
            />
          </Button>
        </div>
        {selectedFilter === "GELOMBANG" &&
          (isPendingListPeriode ? (
            "Loading..."
          ) : (
            <div className="mt-3 grid grid-cols-3 gap-5">
              {listPeriodeStudentExchange?.map((periode: Periode) => {
                const isChecked = filter.gelombang.includes(
                  String(periode.id_sys_periode),
                );

                return (
                  <div key={periode.id_sys_periode} className="flex gap-3">
                    <Checkbox
                      checked={isChecked}
                      onCheckedChange={(checked) => {
                        setFilter((prev) => {
                          const updatedGelombang = checked
                            ? [
                                ...prev.gelombang,
                                String(periode.id_sys_periode),
                              ]
                            : prev.gelombang.filter(
                                (gelombang) =>
                                  gelombang !== String(periode.id_sys_periode),
                              );

                          return {
                            ...prev,
                            gelombang: updatedGelombang,
                          };
                        });
                      }}
                    />
                    <Label>Gelombang ke-{periode.gelombang_show} </Label>
                  </div>
                );
              })}
            </div>
          ))}
      </section>

      <section className="p-3 relative border rounded-xl max-h-[50vh] overflow-auto">
        <div
          className="flex justify-between items-center"
          onClick={() =>
            setSelectedFilter((prev) => (prev === "PROGRAM" ? "" : "PROGRAM"))
          }
        >
          <span className="font-bold">Program</span>
          <Button type="button" size={"icon"} variant={"outline"}>
            <ChevronRight
              className={`${
                selectedFilter === "PROGRAM" ? "rotate-90" : ""
              } duration-300`}
            />
          </Button>
        </div>
        {selectedFilter === "PROGRAM" &&
          (isPendingDataProgramStudentExchange ? (
            "Loading..."
          ) : (
            <>
              <div className="py-3 sticky top-0">
                <Input
                  placeholder="Search..."
                  value={searchQueryProdi}
                  onChange={(event) => setSearchQueryProdi(event.target.value)}
                />
              </div>
              <div className="mt-3 grid grid-cols-2 gap-5">
                {dataProgramStudentExchange?.map(
                  (program: RefDaftarProgram) => {
                    const isChecked = filter.program.includes(
                      program.id_ref_daftar_program,
                    );

                    return (
                      <div
                        key={program.id_ref_daftar_program}
                        className="flex gap-3"
                      >
                        <Checkbox
                          checked={isChecked}
                          onCheckedChange={(checked) => {
                            setFilter((prev) => {
                              const programArray = [...prev.program];

                              if (checked) {
                                if (
                                  !programArray.includes(
                                    program.id_ref_daftar_program,
                                  )
                                ) {
                                  programArray.push(
                                    program.id_ref_daftar_program,
                                  );
                                }
                              } else {
                                const index = programArray.indexOf(
                                  program.id_ref_daftar_program,
                                );
                                if (index > -1) {
                                  programArray.splice(index, 1);
                                }
                              }

                              return {
                                ...prev,
                                program: programArray,
                              };
                            });
                          }}
                        />
                        <Label>{program.nama_program}</Label>
                      </div>
                    );
                  },
                )}
              </div>
            </>
          ))}
      </section>

      <div className="absolute bottom-3 left-3 right-3">
        <div className="flex justify-between">
          <div className="flex gap-3">
            <Button
              onClick={() => closeModal()}
              type="button"
              variant={"outline"}
            >
              Tutup
            </Button>
            <Button>Terapkan Filter</Button>
          </div>
          <Button
            type="button"
            variant={"outline"}
            onClick={() => {
              setFilter((prev) => ({
                ...prev,
                program: [],
                gelombang: [],
                status_validasi: "",
                status_kelulusan: "",
                status_daftarkembali: "",
              }));
              closeModal();
              navigate("/operator-studentexchange/data-pendaftaran");
            }}
          >
            Reset
          </Button>
        </div>
      </div>
    </form>
  );
}
